
import { SvarData } from "@/types/riskbedomning";
import { v4 as uuidv4 } from "uuid";
import { getActivePhase } from "@/hooks/useCurrentPhase";

// Fields to track for revision history
const TRACKED_FIELDS: Array<{ key: keyof SvarData; label: string }> = [
  { key: "svar", label: "Svar" },
  { key: "kommentar", label: "Kommentar" },
  { key: "identifieradRisk", label: "Identifierad risk" },
  { key: "organisatoriskAtgard", label: "Organisatorisk åtgärd" },
  { key: "tekniskAtgard", label: "Teknisk åtgärd" },
  { key: "sannolikhet", label: "Sannolikhet" },
  { key: "konsekvens", label: "Konsekvens" },
  { key: "isHandled", label: "Hanterad" },
  { key: "isTransferred", label: "Överförd" },
  { key: "acceptedInProduction", label: "Accepterad i produktion" },
  { key: "isProductionHandled", label: "Produktionshanterad" },
  { key: "answeredBy", label: "Besvarad av" },
  { key: "answererRole", label: "Besvarares roll" },
];

export function formatFieldValue(value: any): string {
  if (value === null || value === undefined) return "–";
  if (typeof value === "boolean") return value ? "Ja" : "Nej";
  if (typeof value === "string") {
    if (value === "ja") return "Ja";
    if (value === "nej") return "Nej";
    if (value === "na") return "N/A";
    return value.length > 60 ? value.substring(0, 57) + "..." : value;
  }
  if (typeof value === "number") return String(value);
  return JSON.stringify(value);
}

export function getFieldLabel(field: string): string {
  const found = TRACKED_FIELDS.find((f) => f.key === field);
  return found ? found.label : field;
}

/**
 * Compare old and new answer data and create a revision entry if anything changed.
 * Returns the new svar with the revision appended, or the original newSvar if nothing changed.
 */
export function createRevisionEntry(
  oldSvar: SvarData | undefined,
  newSvar: SvarData,
  user: { id: string; name: string },
  phase?: { key: string | null; name: string }
): SvarData {
  const old = oldSvar || {};
  const changes: Array<{ field: string; oldValue: any; newValue: any }> = [];

  for (const { key } of TRACKED_FIELDS) {
    const oldVal = (old as any)[key];
    const newVal = (newSvar as any)[key];

    // Skip if both are empty
    if (
      (oldVal === undefined || oldVal === null || oldVal === "") &&
      (newVal === undefined || newVal === null || newVal === "")
    ) {
      continue;
    }

    // Compare stringified for objects, direct for primitives
    const oldStr = typeof oldVal === "object" ? JSON.stringify(oldVal) : oldVal;
    const newStr = typeof newVal === "object" ? JSON.stringify(newVal) : newVal;

    if (oldStr !== newStr) {
      changes.push({ field: key, oldValue: oldVal, newValue: newVal });
    }
  }

  if (changes.length === 0) {
    return newSvar;
  }

  // Skedet fryses i loggen – det ska stå kvar även när projektet går vidare.
  const activePhase = phase ?? getActivePhase();

  const revision = {
    id: uuidv4(),
    timestamp: new Date().toISOString(),
    userId: user.id,
    userName: user.name,
    phaseKey: activePhase.key ?? null,
    phaseName: activePhase.name || "Ej angivet",
    changes,
  };

  const existingRevisions = newSvar.revisions || [];
  return {
    ...newSvar,
    revisions: [...existingRevisions, revision],
  };
}
